import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { sendEvent } from 'app/services/event';
import { NAV_ID_TO_TAB_EVENT } from 'app/tracking';
import config from '../../config.json';

const REGION_CODES = {
  canada: 'CA',
  uk: 'UK',
} as const;

const DEFAULT_EVENT_OPTIONS: EventOptions = {};

interface EventOptions {
  type?: string;
  tab?: string;
  section?: string;
  [key: string]: any;
}

interface RootState {
  pharmacySelector: {
    selectedPharmacyId: string;
    selectedPharmacy: {
      chain_id?: string;
    } | null;
  };
}

/**
 * Hook for tracking events with automatic context
 * Note: user_id, user_roles, and pharmacy_id are derived by the backend from the JWT token and URL path
 * @param screen The screen/page name where events are being tracked
 * @param defaultOptions Default options to include with every event. 
 *                       IMPORTANT: Pass a stable reference (e.g., using useMemo or a constant) to avoid recreating the tracker function on every render.
 * @returns A function to track events with the signature (action, options?)
 */
export default function useEventTracker(screen?: string, defaultOptions: EventOptions = DEFAULT_EVENT_OPTIONS) {
  const selectedPharmacyId = useSelector((state: RootState) => state.pharmacySelector.selectedPharmacyId);
  const selectedPharmacy = useSelector((state: RootState) => state.pharmacySelector.selectedPharmacy);
  const location = useLocation();

  // Use defaultOptions directly - callers must provide stable references
  const stableDefaultOptions = useMemo(() => defaultOptions, [defaultOptions]);

  const trackEvent = useCallback(
    (action: string, options: EventOptions = {}) => {
      if (!selectedPharmacyId) {
        console.warn('Cannot track event: No pharmacy selected');
        return Promise.resolve();
      }

      // Use screen from options if provided, otherwise use hook's screen param, otherwise use current location
      const eventScreen = options.screen || screen || location.pathname;
      
      // Remove screen from options since it's a separate parameter to sendEvent
      const { screen: _, ...optionsWithoutScreen } = options;
      
      // Map deployment region to standardized region code
      const deploymentRegion = (config as any).deploymentRegion;
      const regionCode = deploymentRegion ? REGION_CODES[deploymentRegion as keyof typeof REGION_CODES] : undefined;
      
      // Build the event payload
      // Note: user_id, user_roles, and pharmacy_id are derived by backend from JWT and URL
      const eventPayload: EventOptions = {
        ...stableDefaultOptions,
        ...optionsWithoutScreen,
        // Only include data not available from JWT or URL path
        ...(selectedPharmacy?.chain_id && { org_id: selectedPharmacy.chain_id }),
        ...(regionCode && { region: regionCode }),
      };

      return sendEvent(selectedPharmacyId, eventScreen, action, eventPayload);
    },
    [selectedPharmacyId, selectedPharmacy, location.pathname, screen, stableDefaultOptions]
  );

  return trackEvent;
}

/**
 * Specialized hook for tracking navigation item clicks
 * Handles lookup, URL normalization, and tracking event construction
 * @returns A function to track navigation with the signature (navigationItem)
 */
export function useNavigationTracker() {
  const trackEvent = useEventTracker();
  
  return useCallback(
    (navigationItem: { url?: string; id?: string }) => {
      // Look up the tab event for this navigation item
      const itemId = navigationItem?.id;
      if (!itemId) return Promise.resolve();
      
      const tabEvent = (NAV_ID_TO_TAB_EVENT as Record<string, any>)[itemId];
      
      // Early return if this nav item doesn't have tracking
      if (!tabEvent) return Promise.resolve();
      
      // Normalize the URL for tracking
      let targetScreen = '/';
      
      if (navigationItem.url) {
        if (navigationItem.url.startsWith('http://') || navigationItem.url.startsWith('https://')) {
          // Keep full HTTP(s) URLs unchanged
          targetScreen = navigationItem.url;
        } else {
          // Normalize relative paths: strip leading slashes and add exactly one
          const normalizedPath = navigationItem.url.replace(/^\/+/, '');
          targetScreen = normalizedPath ? `/${normalizedPath}` : '/';
        }
      }
      
      return trackEvent(tabEvent.action, { ...tabEvent, screen: targetScreen });
    },
    [trackEvent]
  );
}